import { GLOBAL_ASSETS_SENTINEL_OWNER_ID } from '@cadstrom/cadstrom-shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';

import { ManufacturerPicker } from '#src/components/manufacturer-picker/manufacturer-picker.js';
import { OwnerOrganizationPicker } from '#src/components/owner-organization-picker/owner-organization-picker.js';
import { useTRPC } from '#src/trpc.js';

import type { FormEvent } from 'react';

const routeApi = getRouteApi('/_authorization-gate/');

export type NewPackageFamilyDialogProps = {
	onFinished: () => void;
};

export function NewPackageFamilyForm({ onFinished }: NewPackageFamilyDialogProps) {
	const navigate = routeApi.useNavigate();
	const trpc = useTRPC();

	const [nameDraft, setNameDraft] = useState('');
	const [manufacturerId, setManufacturerId] = useState<string | { create: string } | undefined>(undefined);
	const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);

	const formIsValid = nameDraft.length > 0 && manufacturerId !== undefined && ownerId !== undefined;

	const { mutateAsync: createPackageFamily } = useMutation(trpc.packageFamily.create.mutationOptions());

	const { mutateAsync: createManufacturer } = useMutation(trpc.manufacturer.create.mutationOptions());

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!formIsValid) {
			return;
		}

		setIsSubmitting(true);
		try {
			let finalManufacturerId: string;
			if (typeof manufacturerId === 'object') {
				finalManufacturerId = (await createManufacturer({ name: manufacturerId.create })).manufacturer.id;
			} else {
				finalManufacturerId = manufacturerId;
			}

			const { pkgFamily } = await createPackageFamily({
				name: nameDraft,
				manufacturerId: finalManufacturerId,
				ownerId,
				alsoCreatePackage: true,
			});

			onFinished();
			await navigate({ to: '/package-store/$packageFamilyId', params: { packageFamilyId: pkgFamily.id } });
		} catch (error) {
			console.error(error);
			setIsSubmitting(false);

			if (error instanceof TRPCClientError) {
				setSubmissionError(error.message);
			} else {
				setSubmissionError('An unknown error occurred.');
			}
		}
	};

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault();
				handleSubmit(event).catch((error: unknown) => {
					throw error;
				});
			}}
			className='m-6 flex flex-col gap-5'
		>
			<h2 className='text-xl font-semibold'>New Package Family</h2>

			<div className='mb-1 flex w-120 flex-col gap-3'>
				<TextField
					label='Name'
					autoFocus
					disabled={isSubmitting}
					ref={(node) => node?.focus()}
					value={nameDraft}
					onChange={(event) => setNameDraft(event.target.value)}
				/>
				<ManufacturerPicker disabled={isSubmitting} value={manufacturerId} onChange={setManufacturerId} />
				<OwnerOrganizationPicker
					disabled={isSubmitting}
					value={ownerId}
					onChange={setOwnerId}
					globalOptionPosition='first'
				/>
				{ownerId === GLOBAL_ASSETS_SENTINEL_OWNER_ID && (
					<Alert severity='warning' className='max-w-120'>
						<div className='flex flex-col gap-2'>
							<p className='min-h-0'>Global packages are visible to every Cadstrom customer.</p>
							<p className='min-h-0'>Ensure this is not a confidential package.</p>
						</div>
					</Alert>
				)}
			</div>

			{submissionError !== undefined && (
				<Alert severity='error' className='-my-2 max-h-32 max-w-120' onClose={() => setSubmissionError(undefined)}>
					<p className='min-h-0'>{submissionError}</p>
				</Alert>
			)}

			<div className='flex justify-end gap-2'>
				<Button variant='outlined' disabled={isSubmitting} onClick={onFinished}>
					<p>Cancel</p>
				</Button>
				<Button type='submit' loading={isSubmitting} disabled={!formIsValid}>
					<p>Create</p>
				</Button>
			</div>
		</form>
	);
}
